import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import type { SourceCatalogState } from "../../hooks/useSourceCatalog";
import { ErrorToast } from "../ErrorToast";
import { LoadingSpinner } from "../LoadingSpinner";
import { SourceAssetCard } from "./SourceAssetCard";
import { SourceAssetDetailsModal } from "./SourceAssetDetailsModal";

/**
 * The catalog as a card grid that fetches the next page as its end scrolls
 * into view (the same sentinel pattern as ``GalleryDesignGrid``).
 */
export function SourceAssetGrid({
  catalog,
  emptyMessage,
}: {
  catalog: SourceCatalogState;
  /** Shown when there is nothing to list. */
  emptyMessage: string;
}) {
  const { hasMore, isFetchingMore, loadMore } = catalog;
  const { ref: sentinelRef, inView } = useInView({ rootMargin: "200px" });
  useEffect(() => {
    if (inView && hasMore && !isFetchingMore) {
      loadMore();
    }
  }, [inView, hasMore, isFetchingMore, loadMore]);

  if (catalog.isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <LoadingSpinner text="Loading catalog..." size="lg" />
      </div>
    );
  }

  if (catalog.isError) {
    return (
      <div className="flex min-h-96 flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-[var(--accent-destructive)]">
          Couldn&apos;t load the catalog.
        </p>
        <button
          type="button"
          onClick={catalog.refetch}
          className="text-sm font-semibold text-[var(--accent-primary)] hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (catalog.assets.length === 0) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <p className="text-sm text-[var(--text-secondary)]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <SourceAssetCards catalog={catalog} />
      <div ref={sentinelRef} aria-hidden="true" />
      {isFetchingMore && (
        <div className="flex justify-center py-6">
          <LoadingSpinner size="md" />
        </div>
      )}
    </>
  );
}

/** The cards, the details modal any of them opens, and the save-error toast. */
function SourceAssetCards({ catalog }: { catalog: SourceCatalogState }) {
  // By id, so the modal reads the live catalog entry: a save there flips
  // ``in_library`` like it does on the card.
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const detailsAsset = catalog.assets.find((asset) => asset.id === detailsId);
  // A refetch that drops the product closes its details for good, rather
  // than reopening them if it comes back.
  if (detailsId !== null && !detailsAsset) setDetailsId(null);

  return (
    <>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(192px,1fr))] gap-x-4 gap-y-6">
        {catalog.assets.map((asset) => (
          <SourceAssetCard
            key={asset.id}
            asset={asset}
            onSave={catalog.saveToLibrary}
            onShowDetails={setDetailsId}
          />
        ))}
      </ul>
      {detailsAsset && (
        <SourceAssetDetailsModal
          asset={detailsAsset}
          catalog={catalog}
          onClose={() => setDetailsId(null)}
        />
      )}
      {/* After the modal, so a failure there shows above it. */}
      <ErrorToast
        message={catalog.saveError}
        onDismiss={catalog.dismissSaveError}
      />
    </>
  );
}
